import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ArrowRight, Calendar, User, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { blogPostingSchema } from "@/lib/seo-schema";

/**
 * Blog index for cameraclubscore.com — public, unauthenticated.
 *
 * New posts: add a JSX component in `posts/`, then register it in POSTS
 * below. The renderer at `/blog/:slug` looks up by slug. Keep this list
 * ordered newest-first — it's also what appears on the homepage teaser.
 *
 * Scheduling: `publish_at` (ISO date) hides a post from the index until
 * that date has passed. Once past, it appears at the top of the list.
 * Set to a past date to publish immediately. This lets us write a
 * pipeline of posts all at once and have them "drip" naturally over
 * weeks so the blog looks actively maintained.
 */
export const POSTS = [
  {
    slug: "why-spreadsheets-fail-camera-clubs",
    title: "Why running photo competitions on a spreadsheet is broken — and what we did about it",
    excerpt:
      "Every camera club has been there. It's judging night, the projector's warming up, and someone realises the spreadsheet doesn't add up. Here's why the old way is more fragile than it looks, and what a purpose-built tool changes.",
    date: "2026-02-15",
    publish_at: "2026-02-15",
    author: "Camera Club Score",
    read_minutes: 6,
    tag: "For committees",
  },
  {
    slug: "score-a-competition-in-5-minutes",
    title: "Score a camera club competition in 5 minutes with Camera Club Score",
    excerpt:
      "From opening the app to a shareable results page — here's the fastest possible walkthrough of scoring one competition on the night, with a room full of members watching.",
    date: "2026-02-15",
    publish_at: "2026-02-15",
    author: "Camera Club Score",
    read_minutes: 5,
    tag: "How-to",
  },
  {
    slug: "organising-a-camera-club-competition",
    title: "How to organise a camera club photo competition — the complete guide",
    excerpt:
      "Everything a committee needs to know about setting up, running and closing out a photography competition. Categories, rules, entry management, judging, scoring, and how to keep it all fair.",
    date: "2026-07-07",
    publish_at: "2026-07-07",
    author: "Camera Club Score",
    read_minutes: 12,
    tag: "Getting started",
  },
  {
    slug: "competition-secretary-field-manual",
    title: "What a Competition Secretary actually does — the field manual",
    excerpt:
      "The Competition Secretary is the quiet hero of every club season. Here's what the role really involves, week by week, and the shortcuts that make it less of a burden.",
    date: "2026-07-21",
    publish_at: "2026-07-21",
    author: "Camera Club Score",
    read_minutes: 11,
    tag: "Committee",
  },
  {
    slug: "judging-and-scoring-photography-competitions",
    title: "Judging and scoring photography competitions — the fair way",
    excerpt:
      "Score out of 10 or 20? Should judges see previous scores? How do we handle ties? A committee's guide to designing a judging system your members actually trust.",
    date: "2026-08-04",
    publish_at: "2026-08-04",
    author: "Mick Sadler, founder",
    read_minutes: 10,
    tag: "Judging",
  },
  {
    slug: "pdi-projected-digital-image-competitions",
    title: "PDI (Projected Digital Image) competitions from A to Z",
    excerpt:
      "Image sizes, colour spaces, file naming, projector calibration, submissions. Everything a camera club needs to run PDI competitions that look great and land smoothly.",
    date: "2026-08-18",
    publish_at: "2026-08-18",
    author: "Camera Club Score",
    read_minutes: 9,
    tag: "PDI",
  },
  {
    slug: "print-competitions-without-losing-prints",
    title: "Running print competitions without losing prints (or your sanity)",
    excerpt:
      "Labelling, tracking, returning, and recording — the practical field guide to print competitions in a modern club. Yes, they still matter. Here's how to keep them tidy.",
    date: "2026-09-01",
    publish_at: "2026-09-01",
    author: "Camera Club Score",
    read_minutes: 8,
    tag: "Print",
  },
  {
    slug: "season-league-tables-and-photographer-of-the-year",
    title: "Season league tables, Photographer of the Year, and other annual awards",
    excerpt:
      "How to design a season that produces fair, exciting, and defensible annual awards. Sum totals vs best-N, tie-breaks, honourable mentions, and how software makes this a 5-minute job.",
    date: "2026-09-15",
    publish_at: "2026-09-15",
    author: "Camera Club Score",
    read_minutes: 10,
    tag: "League tables",
  },
  {
    slug: "what-to-look-for-in-camera-club-competition-software",
    title: "What to look for in camera club competition software",
    excerpt:
      "A buyer's guide from someone who's built one. What matters, what doesn't, and the ten questions to ask any tool before you commit your club's data to it.",
    date: "2026-09-29",
    publish_at: "2026-09-29",
    author: "Mick Sadler, founder",
    read_minutes: 10,
    tag: "Buyer's guide",
  },
  {
    slug: "troubleshooting-and-best-practices",
    title: "Troubleshooting & best practices for camera club competitions",
    excerpt:
      "The stuff that goes wrong on judging night, at season end, and during committee handovers — and the ten practices that stop 90% of it from ever happening.",
    date: "2026-10-13",
    publish_at: "2026-10-13",
    author: "Mick Sadler, founder",
    read_minutes: 9,
    tag: "Best practices",
  },
];



function SubscribeForm() {
  // Public email opt-in for the biweekly blog drip. Best-effort push to
  // Brevo — the endpoint always returns ok if the address landed in our
  // own store, so users see a consistent success message even if the
  // Brevo API is momentarily unreachable.
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      await api.post("/blog/subscribe", { email: trimmed });
      setDone(true);
      setEmail("");
      toast.success("You're on the list — thanks for reading.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't subscribe. Try again?");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        className="ccs-card border-amber-400/30 bg-amber-400/5 text-center"
        data-testid="blog-subscribe-thanks"
      >
        <div className="inline-flex items-center gap-2 text-amber-400 text-sm mono uppercase mb-2">
          <Mail size={14} /> Subscribed
        </div>
        <h3 className="text-xl font-semibold mb-2">Thanks &mdash; you&apos;re in.</h3>
        <p className="muted-text text-sm">
          We&apos;ll drop a note when the next post goes live. Roughly every
          two weeks. No spam, no upsells, unsubscribe with one click.
        </p>
      </div>
    );
  }

  return (
    <div className="ccs-card" data-testid="blog-subscribe-card">
      <div className="inline-flex items-center gap-2 text-amber-400 text-sm mono uppercase mb-2">
        <Mail size={14} /> Get new posts by email
      </div>
      <h3 className="text-xl font-semibold mb-2">Biweekly, plain text, no spam.</h3>
      <p className="muted-text text-sm mb-4">
        A short note whenever we publish something new about running
        camera club competitions. One click to unsubscribe, and we don&apos;t
        share your address with anyone.
      </p>
      <form
        onSubmit={onSubmit}
        className="flex flex-col sm:flex-row gap-2"
        data-testid="blog-subscribe-form"
      >
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@yourcameraclub.co.uk"
          disabled={busy}
          className="flex-1 bg-black/40 border border-neutral-800 rounded-full px-4 py-2 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-amber-400/50 disabled:opacity-50"
          data-testid="blog-subscribe-email"
          aria-label="Email address"
        />
        <button
          type="submit"
          disabled={busy || !email.trim()}
          className="inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 disabled:bg-neutral-700 disabled:text-neutral-500 text-black rounded-full px-5 py-2 text-sm font-medium transition-colors"
          data-testid="blog-subscribe-submit"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : null}
          {busy ? "Subscribing…" : "Subscribe"}
        </button>
      </form>
    </div>
  );
}


export default function BlogIndex() {
  // Only show posts whose publish_at is today or in the past. Future
  // posts are staged in the array but hidden until their date rolls
  // around — no CMS, no cron, just a date compare on render.
  const today = new Date().toISOString().slice(0, 10);
  const visible = POSTS
    .filter((p) => (p.publish_at || p.date) <= today)
    .sort((a, b) => (b.publish_at || b.date).localeCompare(a.publish_at || a.date));
  return (
    <div className="min-h-screen bg-black text-white" data-testid="blog-index-page">
      <Helmet>
        <title>Blog — Camera Club Score</title>
        <meta
          name="description"
          content="Practical writing on running camera club competitions well. Guides for secretaries and chairs on PDI, print, PDI software imports, scoring rules, and league tables."
        />
        <link rel="canonical" href="https://cameraclubscore.com/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cameraclubscore.com/blog" />
        <meta property="og:title" content="Blog — Camera Club Score" />
        <meta
          property="og:description"
          content="Notes from the darkroom — practical writing on running UK camera club photo competitions."
        />
        <meta property="og:image" content="https://cameraclubscore.com/og-image.png" />
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-1 muted-text text-sm hover:text-amber-400 mb-8"
          data-testid="blog-back-home"
        >
          <ArrowLeft size={14} /> Back to home
        </Link>

        <header className="mb-12">
          <div className="ccs-label mb-2">The Camera Club Score blog</div>
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4">
            Notes from the darkroom
          </h1>
          <p className="muted-text text-base max-w-2xl">
            Practical writing on running camera club competitions well.
            Aimed at secretaries, chairs, and anyone who&apos;s ever spent a
            Friday night wrestling with a spreadsheet.
          </p>
        </header>

        <div className="space-y-6">
          {visible.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              className="ccs-card block hover:border-amber-400/50 transition-colors"
              data-testid={`blog-card-${p.slug}`}
            >
              <div className="flex items-center gap-3 mb-2 text-xs muted-text">
                <span className="mono uppercase text-amber-400">{p.tag}</span>
                <span>·</span>
                <span className="inline-flex items-center gap-1">
                  <Calendar size={12} /> {fmtDate(p.date)}
                </span>
                <span>·</span>
                <span>{p.read_minutes} min read</span>
              </div>
              <h2 className="text-xl md:text-2xl font-semibold mb-2">
                {p.title}
              </h2>
              <p className="muted-text text-sm mb-3">{p.excerpt}</p>
              <span className="inline-flex items-center gap-1 text-amber-400 text-sm">
                Read <ArrowRight size={14} />
              </span>
            </Link>
          ))}
        </div>

        <div className="mt-12">
          <SubscribeForm />
        </div>

        <footer className="mt-16 text-center">
          <p className="muted-text text-sm">
            Have an idea for a post? Email{" "}
            <a
              href="mailto:mail@cameraclubscore.com"
              className="text-amber-400 hover:underline"
            >
              mail@cameraclubscore.com
            </a>
            .
          </p>
        </footer>
      </div>
    </div>
  );
}


export function BlogPostShell({ post, children }) {
  // BlogPosting JSON-LD (upgraded from Article) so answer engines pick
  // this up as authored content, not a generic web page. The `speakable`
  // spec tells voice assistants + Google's "Interesting things" feature
  // exactly which selectors carry the answer-shaped copy (`.lead` intro
  // and `h2` question headings).
  const jsonLd = blogPostingSchema(post);
  return (
    <div className="min-h-screen bg-black text-white" data-testid="blog-post-page">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1 muted-text text-sm hover:text-amber-400 mb-8"
          data-testid="blog-post-back"
        >
          <ArrowLeft size={14} /> All posts
        </Link>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4 text-xs muted-text">
            <span className="mono uppercase text-amber-400">{post.tag}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1">
              <Calendar size={12} /> {fmtDate(post.date)}
            </span>
            <span>·</span>
            <span className="inline-flex items-center gap-1">
              <User size={12} /> {post.author}
            </span>
            <span>·</span>
            <span>{post.read_minutes} min read</span>
          </div>
          <h1
            className="text-3xl md:text-5xl font-semibold tracking-tight leading-tight mb-4"
            data-testid="blog-post-title"
          >
            {post.title}
          </h1>
        </div>

        <article className="blog-prose">{children}</article>

        <footer className="mt-16 pt-8 border-t border-neutral-900">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="text-sm muted-text">
              Want to try Camera Club Score for your club?
            </div>
            <Link
              to="/register"
              className="ccs-btn inline-flex items-center gap-2"
              data-testid="blog-cta-register"
            >
              Start 30-day free trial <ArrowRight size={14} />
            </Link>
          </div>
        </footer>
      </div>
    </div>
  );
}


function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch (_) {
    return iso;
  }
}
